import { RefObject, useLayoutEffect, useMemo, useState } from 'react';
import { useMotionValueEvent, useScroll } from 'motion/react';

type ScrollPosition = 'start' | 'middle' | 'end';

export function useScrollState<E extends HTMLElement = HTMLDivElement>(
  ref: RefObject<E | null>
) {
  const [canScrollX, setCanScrollX] = useState(false);
  const [canScrollY, setCanScrollY] = useState(false);

  const [scrollXPosition, setScrollXPosition] =
    useState<ScrollPosition>('start');
  const [scrollYPosition, setScrollYPosition] =
    useState<ScrollPosition>('start');

  const scroll = useScroll({
    container: ref,
  });

  useMotionValueEvent(scroll.scrollXProgress, 'change', (value) => {
    if (value <= 0.01) {
      setScrollXPosition('start');
    } else if (value >= 0.99) {
      setScrollXPosition('end');
    } else {
      setScrollXPosition('middle');
    }
  });

  useMotionValueEvent(scroll.scrollYProgress, 'change', (value) => {
    if (value <= 0.01) {
      setScrollYPosition('start');
    } else if (value >= 0.99) {
      setScrollYPosition('end');
    } else {
      setScrollYPosition('middle');
    }
  });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const container = ref.current;
    function handleResize() {
      setCanScrollX(container.scrollWidth > container.clientWidth);
      setCanScrollY(container.scrollHeight > container.clientHeight);
    }
    const observer = new ResizeObserver(handleResize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return useMemo(
    () => ({
      canScrollX,
      canScrollY,
      scrollXPosition,
      scrollYPosition,
    }),
    [canScrollX, canScrollY, scrollXPosition, scrollYPosition]
  );
}
